import {
    durationToMinutes,
    minutesToDuration,
    numericInputProps,
    toApiDateTime,
    toInputDateTime,
} from './jobs';

export const MAINTENANCE_TYPES = [
    { value: 'manutencao_corretiva', label: 'Manutenção corretiva' },
    { value: 'manutencao_preventiva', label: 'Manutenção preventiva' },
    { value: 'limpeza', label: 'Limpeza' },
    { value: 'erro_em_investigacao', label: 'Erro em investigação' },
];

export const DEFAULT_MAINTENANCE_DURATION = '00:00';

export { durationToMinutes, minutesToDuration, numericInputProps };

export function emptyPartRow() {
    return { name: '', value_cents: 0, stock_part_uuid: '' };
}

export function partName(part) {
    if (typeof part === 'string') {
        return part.trim();
    }
    return String(part?.name || '').trim();
}

export function partValueCents(part) {
    if (part == null || typeof part === 'string') {
        return 0;
    }
    const raw = part.value_cents ?? part.valueCents ?? part.value ?? 0;
    const cents = Number(raw);
    if (!Number.isFinite(cents)) {
        return 0;
    }
    return Math.max(0, Math.round(cents));
}

export function partStockUuid(part) {
    if (part == null || typeof part === 'string') {
        return '';
    }
    return String(part.stock_part_uuid || part.stockPartUuid || '').trim();
}

export function replacementPartsCount(parts) {
    if (!Array.isArray(parts)) {
        return 0;
    }
    return parts.filter((part) => partName(part)).length;
}

export function maintenancePartsTotalCents(parts) {
    if (!Array.isArray(parts)) {
        return 0;
    }
    return parts.reduce((sum, part) => sum + partValueCents(part), 0);
}

export function maintenanceCountLabel(count) {
    const n = Number(count) || 0;
    return n === 1 ? '1 manutenção' : `${n} manutenções`;
}

export function replacementPartsCountLabel(count) {
    const n = Number(count) || 0;
    return n === 1 ? '1 peça' : `${n} peças`;
}

export function formatReplacementPartNames(parts) {
    const names = (parts || []).map(partName).filter(Boolean);
    return names.length ? names.join(', ') : 'Nenhuma';
}

export function maintenanceTypeLabel(value) {
    const found = MAINTENANCE_TYPES.find((type) => type.value === value);
    return found ? found.label : value || '—';
}

export function getMaintenanceTypeBadgeProps(type) {
    const config = {
        manutencao_corretiva: { color: 'error' },
        manutencao_preventiva: { color: 'info' },
        limpeza: { color: 'success' },
        erro_em_investigacao: { color: 'warning' },
    }[type] || { color: 'default' };

    return {
        label: maintenanceTypeLabel(type),
        size: 'small',
        color: config.color,
    };
}

/**
 * Agrega manutenções por impressora (soma análise + correção em minutos).
 * Ordenação exclusiva por downtime total — quantidade de ocorrências não define a posição.
 */
export function buildMaintenanceSummaryCards(printers, maintenances) {
    const summary = {};
    (maintenances || []).forEach((row) => {
        const printerId = row.printer_uuid;
        if (!printerId) {
            return;
        }
        if (!summary[printerId]) {
            summary[printerId] = { count: 0, downtimeMinutes: 0, partsCount: 0, partsTotalCents: 0 };
        }
        summary[printerId].count += 1;
        const analysis = parseInt(row.analysis_duration_minutes, 10) || 0;
        const correction = parseInt(row.correction_duration_minutes, 10) || 0;
        summary[printerId].downtimeMinutes += analysis + correction;
        summary[printerId].partsCount += replacementPartsCount(row.replacement_parts);
        summary[printerId].partsTotalCents += maintenancePartsTotalCents(row.replacement_parts);
    });

    return (printers || [])
        .map((printer) => {
            const stats = summary[printer.id] || {
                count: 0,
                downtimeMinutes: 0,
                partsCount: 0,
                partsTotalCents: 0,
            };
            return {
                printer_uuid: printer.id,
                name: printer.name,
                brand: printer.brand,
                maintenance_count: stats.count,
                downtime_minutes: stats.downtimeMinutes,
                parts_count: stats.partsCount,
                parts_total_cents: stats.partsTotalCents,
                filaments: printer.filaments || [],
            };
        })
        .sort((a, b) => b.downtime_minutes - a.downtime_minutes);
}

export function maintenanceOccurredDay(occurredAt) {
    if (!occurredAt) {
        return '';
    }
    return String(occurredAt).trim().slice(0, 10);
}

export function isMaintenanceInDateRange(occurredAt, from, to) {
    if (!from && !to) {
        return true;
    }
    const day = maintenanceOccurredDay(occurredAt);
    if (!day) {
        return false;
    }
    if (from && day < from) {
        return false;
    }
    if (to && day > to) {
        return false;
    }
    return true;
}

export function filterMaintenancesByDateRange(maintenances, from, to) {
    const rows = maintenances || [];
    if (!from && !to) {
        return rows;
    }
    return rows.filter((row) => isMaintenanceInDateRange(row.occurred_at, from, to));
}

export function formatMaintenancePeriodLabel(from, to) {
    const formatDay = (value) => {
        if (!value) {
            return '';
        }
        const [year, month, day] = String(value).split('-');
        if (!year || !month || !day) {
            return value;
        }
        return `${day}/${month}/${year}`;
    };

    if (from && to) {
        return `${formatDay(from)} a ${formatDay(to)}`;
    }
    if (from) {
        return `a partir de ${formatDay(from)}`;
    }
    if (to) {
        return `até ${formatDay(to)}`;
    }
    return 'Todo o período';
}

/**
 * Totais gerais e extremos por impressora.
 * "Mais" e "menos" consideram só as horas de interrupção; empate de horas lista todas.
 */
export function buildMaintenanceOverviewStats(printers, maintenances) {
    let totalCount = 0;
    let totalMinutes = 0;
    let partsCount = 0;
    let partsTotalCents = 0;

    (maintenances || []).forEach((row) => {
        totalCount += 1;
        const analysis = parseInt(row.analysis_duration_minutes, 10) || 0;
        const correction = parseInt(row.correction_duration_minutes, 10) || 0;
        totalMinutes += analysis + correction;
        partsCount += replacementPartsCount(row.replacement_parts);
        partsTotalCents += maintenancePartsTotalCents(row.replacement_parts);
    });

    const withMaintenance = buildMaintenanceSummaryCards(printers, maintenances)
        .filter((card) => card.maintenance_count > 0);

    let most = [];
    let least = [];
    if (withMaintenance.length > 0) {
        const maxMinutes = Math.max(...withMaintenance.map((card) => card.downtime_minutes));
        const minMinutes = Math.min(...withMaintenance.map((card) => card.downtime_minutes));
        most = withMaintenance.filter((card) => card.downtime_minutes === maxMinutes);
        least = withMaintenance
            .filter((card) => card.downtime_minutes === minMinutes)
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'));
    }

    return {
        totalCount,
        totalMinutes,
        most,
        least,
        partsCount,
        partsTotalCents,
    };
}

/**
 * Totais gerais a partir do resumo agregado por impressora (sem teto de 100).
 */
export function buildMaintenanceOverviewStatsFromSummary(printers, summary) {
    const byPrinter = Array.isArray(summary?.by_printer) ? summary.by_printer : [];
    const cards = buildMaintenanceSummaryCardsFromAggregates(printers, byPrinter);
    const withMaintenance = cards.filter((card) => card.maintenance_count > 0);

    let most = [];
    let least = [];
    if (withMaintenance.length > 0) {
        const maxMinutes = Math.max(...withMaintenance.map((card) => card.downtime_minutes));
        const minMinutes = Math.min(...withMaintenance.map((card) => card.downtime_minutes));
        most = withMaintenance.filter((card) => card.downtime_minutes === maxMinutes);
        least = withMaintenance
            .filter((card) => card.downtime_minutes === minMinutes)
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'));
    }

    return {
        totalCount: Number(summary?.total_count) || 0,
        totalMinutes: Number(summary?.total_downtime_minutes) || 0,
        most,
        least,
        partsCount: Number(summary?.parts_count) || 0,
        partsTotalCents: Number(summary?.parts_total_cents) || 0,
    };
}

/**
 * Cards por impressora a partir de linhas já agregadas no backend.
 */
export function buildMaintenanceSummaryCardsFromAggregates(printers, aggregates) {
    const summary = {};
    (aggregates || []).forEach((row) => {
        const printerId = row.printer_uuid;
        if (!printerId) {
            return;
        }
        summary[printerId] = {
            count: Number(row.maintenance_count) || 0,
            downtimeMinutes: Number(row.downtime_minutes) || 0,
            partsCount: Number(row.parts_count) || 0,
            partsTotalCents: Number(row.parts_total_cents) || 0,
        };
    });

    return (printers || [])
        .map((printer) => {
            const stats = summary[printer.id] || {
                count: 0,
                downtimeMinutes: 0,
                partsCount: 0,
                partsTotalCents: 0,
            };
            return {
                printer_uuid: printer.id,
                name: printer.name,
                brand: printer.brand,
                maintenance_count: stats.count,
                downtime_minutes: stats.downtimeMinutes,
                parts_count: stats.partsCount,
                parts_total_cents: stats.partsTotalCents,
                filaments: printer.filaments || [],
            };
        })
        .sort((a, b) => b.downtime_minutes - a.downtime_minutes);
}

export function buildPrinterMaintenanceFilterQuery({ printerUuid, type, occurredFrom, occurredTo }) {
    const params = new URLSearchParams();
    if (printerUuid) {
        params.set('printer_uuid', printerUuid);
    }
    if (type) {
        params.set('type', type);
    }
    if (occurredFrom) {
        params.set('occurred_from', occurredFrom);
    }
    if (occurredTo) {
        params.set('occurred_to', occurredTo);
    }
    return params.toString();
}

export function buildPrinterMaintenanceListQuery({ page, limit, printerUuid, type, occurredFrom, occurredTo }) {
    const params = new URLSearchParams(buildPrinterMaintenanceFilterQuery({
        printerUuid,
        type,
        occurredFrom,
        occurredTo,
    }));
    params.set('page', String(page));
    params.set('limit', String(limit));
    return params.toString();
}

export function partsToRows(parts) {
    if (!Array.isArray(parts)) {
        return [emptyPartRow()];
    }

    const rows = parts
        .map((part) => ({
            name: partName(part),
            value_cents: partValueCents(part),
            stock_part_uuid: partStockUuid(part),
        }))
        .filter((part) => part.name);

    return rows.length ? rows : [emptyPartRow()];
}

export function rowsToParts(rows) {
    return (rows || [])
        .map((row) => {
            if (typeof row === 'string') {
                const name = row.trim();
                return name || null;
            }
            const name = partName(row);
            if (!name) {
                return null;
            }
            const valueCents = partValueCents(row);
            const stockPartUuid = partStockUuid(row);
            if (valueCents > 0 || stockPartUuid) {
                const item = { name };
                if (valueCents > 0) {
                    item.value_cents = valueCents;
                }
                if (stockPartUuid) {
                    item.stock_part_uuid = stockPartUuid;
                }
                return item;
            }
            return name;
        })
        .filter(Boolean);
}

export function maintenanceFromApi(data) {
    return {
        printer_uuid: data.printer_uuid || '',
        occurred_at: toInputDateTime(data.occurred_at),
        type: data.type || 'manutencao_preventiva',
        problem_description: data.problem_description || '',
        analysis_duration: minutesToDuration(data.analysis_duration_minutes ?? 0),
        performed_actions: data.performed_actions || '',
        printer_hours_count: data.printer_hours_count ?? 0,
        correction_duration: minutesToDuration(data.correction_duration_minutes ?? 0),
    };
}

export function buildMaintenancePayload(form, replacementParts) {
    const payload = {
        printer_uuid: form.printer_uuid,
        type: form.type,
        problem_description: form.problem_description,
        analysis_duration_minutes: durationToMinutes(form.analysis_duration) ?? 0,
        performed_actions: form.performed_actions,
        printer_hours_count: parseInt(form.printer_hours_count, 10) || 0,
        replacement_parts: rowsToParts(replacementParts),
        correction_duration_minutes: durationToMinutes(form.correction_duration) ?? 0,
    };

    const occurredAt = toApiDateTime(form.occurred_at);
    if (occurredAt) {
        payload.occurred_at = occurredAt;
    }

    return payload;
}
